/**
 * ResearchHistorySidebar - past research chats.
 *
 * The last piece the new surface was missing. Loads a page at a time
 * (`researchListChatGroups`, appending on scroll) and buckets the loaded
 * WINDOW by recency - it never claims to show every chat. Opening a chat
 * hands its uuid up; the surface routes to it and the session loads that
 * chat's newest page from SQL, hydrating blobs lazily.
 */

import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { AiOutlineDelete } from 'react-icons/ai';
import { showModal } from '../../../store/actions/modalActions';
import {
  PiDotsThreeOutlineLight,
  PiTrashLight,
  PiUsersLight,
} from 'react-icons/pi';
import { RootState, useAppDispatch } from '../../../store';
import {
  ChatGroupData,
  researchDeleteChatGroup,
  researchListChatGroups,
} from '../../../store/actions/ChatAction';
import RevealingTitle from './RevealingTitle';
import SearchInput from '../../shared/TailwindComponents/SearchInput';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import {
  ensureUTCString,
  formatDateTime,
  formatTime,
  getIntlLocale,
} from '../../../utils/functions';
import {
  CHAT_PAGE_SIZE,
  NEW_SESSION_TITLE,
  bucketChats,
  chatId,
  chatTitle,
  chatTimestamp,
  displayChatTitle,
  filterChats,
  queryCount,
} from './chatList';

/**
 * Relative-ish date for a row, in the reader's own time zone.
 *
 * The backend sends naive timestamps with no zone, which `new Date(…)` reads
 * as LOCAL - so every row was off by the UTC offset. `ensureUTCString` is the
 * platform's existing fix for exactly that, and `formatDateTime` (also
 * platform) does the locale-aware absolute rendering.
 */
export function formatDate(timestamp: string, now: Date): string {
  if (!timestamp) return '';
  const date = new Date(ensureUTCString(timestamp));
  if (Number.isNaN(date.getTime())) return '';
  const days = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (days <= 0) return formatTime(date);
  if (days === 1) return 'Yesterday';
  if (days <= 7) return date.toLocaleDateString(getIntlLocale(), { weekday: 'long' });
  return formatDateTime(timestamp);
}

/**
 * The row's ⋯ menu.
 *
 * Headless UI Menu, as the rest of the platform uses - so keyboard handling,
 * outside-click and focus return behave the way they do everywhere else.
 */
export const ChatRowMenu: React.FC<{
  onShare: () => void;
  onDelete: () => void;
  /** Only the owner may delete. A collaborator is not offered the action. */
  canDelete: boolean;
}> = ({ onShare, onDelete, canDelete }) => {
  const { t } = useTranslation();
  return (
    <Menu as="div" className="relative flex-shrink-0">
      <Menu.Button
        data-testid="chat-menu"
        aria-label={t('Chat actions')}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        className="p-1 rounded-md text-fg-muted opacity-0 group-hover:opacity-100
                   focus:opacity-100 hover:text-fg-strong
                   transition-all duration-200 ease-slide"
      >
        <PiDotsThreeOutlineLight size={14} />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-pop duration-150"
        enterFrom="opacity-0 -translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <Menu.Items className="absolute end-0 z-30 mt-1 w-36 glass rounded-lg
                               shadow-sm p-1 focus:outline-none">
          <Menu.Item>
            {({ active }: { active: boolean }) => (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onShare(); }}
                data-testid="chat-share"
                className={clsx(
                  'flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-start',
                  active ? 'bg-surface-sunken text-fg-strong' : 'text-fg-body'
                )}
              >
                <PiUsersLight className="w-3.5 h-3.5" aria-hidden />
                {t('Share')}
              </button>
            )}
          </Menu.Item>
          {/* Hidden, not disabled: the backend refuses it for a collaborator,
              so offering an inert control would only invite the click. */}
          {canDelete && (
          <Menu.Item>
            {({ active }: { active: boolean }) => (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                data-testid="chat-delete"
                className={clsx(
                  'flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-start',
                  active ? 'bg-danger-50 dark:bg-danger-900/20' : '',
                  'text-danger-600'
                )}
              >
                <AiOutlineDelete className="w-3.5 h-3.5" aria-hidden />
                {t('Delete')}
              </button>
            )}
          </Menu.Item>
          )}
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

interface Props {
  /** The chat currently open, so it can be marked. */
  activeChatUuid: string;
  onSelect: (chatUuid: string) => void;
  onNewChat: () => void;
  onDelete?: (chatUuid: string) => void;
}

const ResearchHistorySidebar: React.FC<Props> = ({
  activeChatUuid,
  onSelect,
  onNewChat,
  onDelete,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const chats = useSelector((s: RootState) => s.model.chatGroups) as ChatGroupData[];

  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  /** A failed load must never look like "you have no chats". */
  const [loadError, setLoadError] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const loadedRef = useRef(false);
  const now = React.useMemo(() => new Date(), []);

  /**
   * Share. Opens the platform's collaborator UI (via GlobalModal) rather than
   * a research-specific one - the control, the roles and the wording are the
   * same as sharing a matter or a table.
   */
  const handleShare = useCallback((chat: ChatGroupData) => {
    dispatch(showModal({
      type: 'researchShare',
      title: t('Share research'),
      message: displayChatTitle(chatTitle(chat)) || t(NEW_SESSION_TITLE),
      chatUuid: chatId(chat),
      chatTitle: displayChatTitle(chatTitle(chat)) || t(NEW_SESSION_TITLE),
      icon: <PiUsersLight className="w-6 h-6 text-info-600" />,
      variant: 'info',
    } as any));
  }, [dispatch, t]);

  /** Delete, behind the canonical destructive confirmation. */
  const handleDelete = useCallback((chat: ChatGroupData) => {
    const id = chatId(chat);
    dispatch(showModal({
      type: 'confirmation',
      title: t('Delete research chat'),
      message: t('This deletes “{{name}}” and its answers.', {
        name: displayChatTitle(chatTitle(chat)) || t(NEW_SESSION_TITLE),
      }),
      subText: t('This cannot be undone.'),
      icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
      variant: 'danger',
      confirmLabel: t('Delete'),
      onConfirm: async () => {
        await dispatch(researchDeleteChatGroup(id));
        onDelete?.(id);
        // Refetch the first page: the list this sidebar renders comes from
        // the store, so this is what makes the row disappear.
        dispatch(researchListChatGroups(1, CHAT_PAGE_SIZE));
      },
    } as any));
  }, [dispatch, t, onDelete]);

  /* First page. Guarded so StrictMode's double-invoke does not double-fetch. */
  const loadFirstPage = useCallback(() => {
    setIsLoading(true);
    setLoadError(false);
    return dispatch(researchListChatGroups(1, CHAT_PAGE_SIZE))
      .then((result) => {
        setPage(1);
        setHasMore(
          result?.has_more ?? (result?.total ? result.total > (result.items?.length || 0) : false)
        );
      })
      .catch(() => {
        // Distinguishable from empty - otherwise a 500 reads as "no chats".
        setHasMore(false);
        setLoadError(true);
      })
      .finally(() => setIsLoading(false));
  }, [dispatch]);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    loadFirstPage();
  }, [loadFirstPage]);

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    const next = page + 1;
    try {
      const result = await dispatch(
        researchListChatGroups(next, CHAT_PAGE_SIZE, undefined, undefined, true)
      );
      setPage(next);
      const loaded = next * CHAT_PAGE_SIZE;
      setHasMore(
        result?.has_more ??
          (result?.total ? result.total > loaded : (result?.items?.length || 0) >= CHAT_PAGE_SIZE)
      );
    } catch {
      setHasMore(false);
    } finally {
      setIsLoadingMore(false);
    }
  }, [dispatch, hasMore, isLoadingMore, page]);

  /* Infinite scroll - the sentinel sits under the last bucket. */
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) loadMore(); },
      { rootMargin: '150px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  const buckets = bucketChats(filterChats(chats || [], query), now);

  return (
    <div
      data-testid="research-history"
      className="h-full min-h-0 flex flex-col"
    >
      <div className="flex-shrink-0 px-3 py-2 border-b border-hairline-soft">
        <SearchInput
          size="small"
          value={query}
          onChange={setQuery}
          placeholder={t('Search…') || ''}
          aria-label={t('Search research chats')}
        />
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {isLoading ? (
          <Skeleton variant="list" rows={6} />
        ) : loadError ? (
          <div className="text-center py-6 space-y-2" data-testid="history-error">
            <p className="text-xs text-fg-muted">{t('Could not load your research chats.')}</p>
            <button
              type="button"
              onClick={loadFirstPage}
              className="text-xs text-fg-muted hover:text-champagne
                         transition-colors duration-200 ease-slide"
            >
              {t('Try again')}
            </button>
          </div>
        ) : buckets.length === 0 ? (
          <p className="text-xs text-fg-muted text-center py-6" data-testid="history-empty">
            {query ? t('No matching chats') : t('No research yet')}
          </p>
        ) : (
          <ul className="space-y-2">
            {buckets.map((bucket) => (
              <React.Fragment key={bucket.label}>
                <li className="glass-bar sticky top-0 z-10 px-2 py-1.5 -mx-2
                               text-[11px] font-semibold uppercase tracking-wide text-fg-muted">
                  {t(bucket.label)}
                </li>
                {bucket.chats.map((chat, i) => {
                  const id = chatId(chat);
                  const isActive = id === activeChatUuid;
                  const count = queryCount(chat);
                  return (
                    <li
                      key={id}
                      data-testid="history-chat"
                      data-active={isActive}
                      style={{ '--reveal-delay': `${Math.min(i, 8) * 35}ms` } as React.CSSProperties}
                      className={clsx(
                        'group relative reveal-item flex items-center justify-between gap-1',
                        'p-2 rounded-lg cursor-pointer border transition-all duration-200 ease-slide',
                        isActive
                          ? 'bg-surface-sunken/80 border-hairline shadow-sm'
                          : 'border-transparent hover:bg-surface-sunken/50 hover:border-hairline-soft'
                      )}
                    >
                      {/* Champagne rail marks the open chat */}
                      <span
                        aria-hidden
                        className={clsx(
                          'absolute start-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-champagne',
                          'transition-opacity duration-200 ease-slide',
                          isActive ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <button
                        type="button"
                        onClick={() => onSelect(id)}
                        className="flex-1 min-w-0 text-start"
                      >
                        {/* The model names the chat while its answer is
                            still being read; the row must not snap from
                            "New Session" to the written title. */}
                        <RevealingTitle
                          text={displayChatTitle(chatTitle(chat)) || t(NEW_SESSION_TITLE)}
                          className="text-xs font-medium text-fg-strong truncate"
                        />
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-xs text-fg-muted">
                            {formatDate(chatTimestamp(chat), now)}
                          </span>
                          {count !== undefined && count > 0 && (
                            <span className="text-[10px] text-fg-muted tabular-nums">
                              · {count} {t(count === 1 ? 'query' : 'queries')}
                            </span>
                          )}
                        </div>
                      </button>
                      <ChatRowMenu
                        onShare={() => handleShare(chat)}
                        onDelete={() => handleDelete(chat)}
                        // `is_owner` comes straight off the list row.
                        canDelete={chat.is_owner !== false}
                      />
                    </li>
                  );
                })}
              </React.Fragment>
            ))}

            {/* The buckets cover what is loaded, not the whole history. */}
            {hasMore && (
              <li
                ref={sentinelRef as any}
                data-testid="history-sentinel"
                className="py-3 flex justify-center"
              >
                {isLoadingMore ? (
                  <div className="w-full"><Skeleton variant="list" rows={2} /></div>
                ) : (
                  <button
                    type="button"
                    onClick={loadMore}
                    className="text-xs text-fg-muted hover:text-champagne
                               transition-colors duration-200 ease-slide"
                  >
                    {t('Load more chats')}
                  </button>
                )}
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ResearchHistorySidebar;
